<template>
  <div
    :class="[
      'custom-select',
      disabled ? 'disabled' : '',
      size === 'sm' ? 'custom-select-sm' : '',
      matchInputStyle ? 'input-style' : '',
      matchInputStyle && selectedOptions.length > 0 ? 'has-value' : '',
    ]"
    :style="computedSelectStyle"
    @keydown="!disabled && handleKeydown"
    @click="toggleDropdown"
  >
    <input
      v-if="searchable"
      type="text"
      v-model="searchQuery"
      @focus="openDropdown"
      @input="filterOptions"
      :class="['custom-select-input']"
      :placeholder="searchPlaceholder"
      :id="id ? id : 'select' + JSON.stringify(options[0])"
      :disabled="disabled"
      :style="{
        backgroundColor: customStyle?.backgroundColor,
        borderRadius: inputBorderRadiusStyle,
      }"
      :autocomplete="autocomplete || 'on'"
    />
    <div v-else-if="multiSelect && selectedOptions.length > 0">
      {{ selectedOptions.map((option) => option.name).join(", ") }}
    </div>
    <div v-else>{{
      selectedOptions[0]?.name || searchQuery || placeholder
    }}</div>
    <div class="icon-container" v-if="!disabled">
      <font-awesome-icon
          v-if="selectedOptions.length === 0"
          class="custom-select-arrow"
          :class="[{ rotate: dropdownOpen }]"
          :icon="['fas', 'chevron-down']"
      />
      <font-awesome-icon
          v-else
          class="custom-select-arrow"
          :class="[{ rotate: dropdownOpen }]"
          :icon="['fas', 'times']"
          @click.stop="clearDropdown"
      />
    </div>
    <div
      v-if="dropdownOpen"
      :class="[
        'custom-select-dropdown',
        'scroll',
      ]"
      @mouseleave="handleMouseLeave"
      @mouseenter="handleMouseEnter"
    >
      <div
        v-if="showSelectAll && multiSelect && !checkAllSelected"
        @click="selectAllOptions"
        class="custom-select-option"
      >
        Select All
      </div>
      <div
        v-for="(option, index) in filteredOptions"
        :key="option.id"
        :class="[
          'custom-select-option',
          {
            selected: isSelected(option),
            highlighted: index === highlightedIndex,
            disabled: disabled,
          },
        ]"
        @click="!disabled && selectOption(option)"
        @mouseover="!disabled && handleMouseOver(index)"
      >
        {{ option.name }}
        <i v-if="isSelected(option)" class="fas fa-check float-right"></i>
      </div>
      <div
        :class="'custom-select-option-disabled'"
        v-if="filteredOptions.length === 0"
      >
        None
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomSelect",
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number, Array, Boolean],
      default: null,
    },
    placeholder: {
      type: String,
      default: "",
    },
    placeholderColor: {
      type: String
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    hoverClose: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: "default",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: [String],
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    enableManualInput: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    matchInputStyle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedOptions: [],
      dropdownOpen: false,
      searchPlaceholder: this.placeholder,
      highlightedIndex: -1,
      hoverTimeout: null,
      inputBorderRadiusStyle: this.matchInputStyle ? "12px" : "6px",
    };
  },
  computed: {
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) {
        return this.options;
      }
      return this.options.filter((option) =>
        option.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
      );
    },
    checkAllSelected() {
      return this.selectedOptions.length === this.options.length;
    },
    computedSelectStyle() {
      if (this.matchInputStyle) {
        const baseStyle = {
          borderRadius: this.inputBorderRadiusStyle,
          fontSize: '20px',
          height: '50px',
          padding: '0 15px',
          border: '2px solid',
          borderColor: 'var(--color-border-input)',
          backgroundColor: 'var(--color-background-input)',
        };
        return {
          ...baseStyle,
          ...this.customStyle,
        };
      }
      return {
        ...this.customStyle,
        borderRadius: this.inputBorderRadiusStyle,
      };
    },
  },
  watch: {
    value(newVal) {
      this.setSelectedOptions(newVal);
    },
    searchQuery(newVal) {
      if (this.enableManualInput && !this.selectedOptions.length) {
        this.$emit("input", newVal);
      }
    },
  },
  methods: {
    test(){
      console.log('test')
    },
    setSelectedOptions(value) {
      if (Array.isArray(value)) {
        this.selectedOptions = this.options.filter((option) =>
          value.includes(option.id),
        );
      } else {
        this.selectedOptions = this.options.filter(
          (option) => option.id === value,
        );
      }
      this.updatePlaceholder();
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.openDropdown();
        this.highlightedIndex = -1;
        this.$emit('open')
      } else {
        this.closeDropdown();
        this.$emit('close')
      }
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.highlightedIndex = -1;
      this.$nextTick(() => {
        this.adjustDropdownPosition();
        const scrollDivs = this.$el.querySelectorAll(".scroll");
        scrollDivs.forEach((div) => {
          div.addEventListener("wheel", this.preventScroll, { passive: false });
        });
      });
    },
    adjustDropdownPosition() {
      const dropdown = this.$el.querySelector(".custom-select-dropdown");
      if (!dropdown) return;

      const rect = this.$el.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const baseRadius = this.matchInputStyle ? "12px" : "6px";
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        dropdown.style.top = "auto";
        dropdown.style.bottom = "100%";
        dropdown.style.borderRadius = `${baseRadius} ${baseRadius} 0 0`;
        this.inputBorderRadiusStyle = `0 0 ${baseRadius} ${baseRadius}`;
      } else {
        dropdown.style.top = "100%";
        dropdown.style.bottom = "auto";
        dropdown.style.borderRadius = `0 0 ${baseRadius} ${baseRadius}`;
        this.inputBorderRadiusStyle = `${baseRadius} ${baseRadius} 0 0`;
      }
    },
    closeDropdown() {
      const scrollDivs = this.$el.querySelectorAll(".scroll");
      scrollDivs.forEach((div) => {
        div.removeEventListener("wheel", this.preventScroll);
      });
      this.dropdownOpen = false;
      this.highlightedIndex = -1;
      this.inputBorderRadiusStyle = this.matchInputStyle ? "12px" : "6px";
    },
    preventScroll(event) {
      const target = event.target;
      event.stopPropagation();
      target.scrollTop += event.deltaY;
    },
    filterOptions() {
      this.dropdownOpen = true;
    },
    handleOutsideClick(event) {
      const dropdown = this.$el;
      if (!dropdown.contains(event.target)) {
        this.closeDropdown();
      }
    },
    handleKeydown(event) {
      switch (event.key) {
        case "Enter":
          if (this.dropdownOpen) {
            if (this.highlightedIndex >= 0) {
              this.selectOption(this.filteredOptions[this.highlightedIndex]);
            }
            this.closeDropdown();
          } else {
            this.openDropdown();
          }
          event.preventDefault();
          break;

        case "ArrowDown":
          if (!this.dropdownOpen) {
            this.openDropdown();
          } else {
            this.highlightedIndex =
              (this.highlightedIndex + 1) % this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "ArrowUp":
          if (this.dropdownOpen) {
            this.highlightedIndex =
              (this.highlightedIndex - 1 + this.filteredOptions.length) %
              this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "Tab":
          this.closeDropdown();
          break;
      }
    },
    handleMouseOver(index) {
      this.highlightedIndex = index;
    },
    selectOption(option) {
      if (this.multiSelect) {
        if (this.isSelected(option)) {
          this.selectedOptions = this.selectedOptions.filter(
            (selected) => selected.id !== option.id,
          );
        } else {
          this.selectedOptions.push(option);
        }
      } else {
        this.selectedOptions = [option];
        this.searchQuery = "";
        this.closeDropdown();
      }
      this.emitSelection();
      this.$forceUpdate()
      this.updatePlaceholder()
    },
    selectAllOptions() {
      this.selectedOptions = [...this.options];
      this.emitSelection();
    },
    updatePlaceholder() {
      this.searchPlaceholder = this.placeholder;
      let extraCount = 0;

      for (let option of this.selectedOptions) {
        if (this.selectedOptions.indexOf(option) >= 2) {
          extraCount++;
          continue;
        }
        if (this.searchPlaceholder === this.placeholder) {
          this.searchPlaceholder = option.name;
        } else {
          this.searchPlaceholder += ", " + option.name;
        }
      }

      if (extraCount > 0) {
        this.searchPlaceholder += ", +" + extraCount;
      }

      const placeholder = document.getElementById(
        this.id ? this.id : "select" + JSON.stringify(this.options[0])
      );

      if (placeholder) {
        let placeholderColor = "#9b9b9b";

        if (this.matchInputStyle && this.selectedOptions.length) {
          placeholderColor = "var(--text-primary)";
        } else if (this.theme === "custom" && this.placeholderColor) {
          placeholderColor = this.placeholderColor;
        } else {
          placeholderColor = "var(--color-border-input)"
        }

        this.$nextTick(() => {
          placeholder.style.setProperty("--placeholder-color", placeholderColor);

          const style = document.createElement("style");
          style.innerHTML = `.custom-select-input::placeholder {color: var(--placeholder-color) !important;}`;
          document.head.appendChild(style);
        });
      }
    },
    emitSelection() {
      let emittedValue;
      if (this.selectedOptions.length) {
        emittedValue = this.multiSelect
          ? this.selectedOptions.map((option) => option.id)
          : this.selectedOptions[0]?.id;
      } else if (this.enableManualInput) {
        emittedValue = this.searchQuery;
      } else {
        emittedValue = this.multiSelect ? [] : null;
      }

      this.$emit("input", emittedValue);
      this.$emit("change", emittedValue);
    },
    isSelected(option) {
      return (
        Array.isArray(this.selectedOptions) &&
        this.selectedOptions.some((selected) => selected.id === option.id)
      );
    },
    clearDropdown() {
      this.selectedOptions = [];
      this.searchQuery = "";
      this.emitSelection();
      this.closeDropdown();
      this.updatePlaceholder()
    },
    handleMouseEnter() {
      clearTimeout(this.hoverTimeout);
    },
    handleMouseLeave() {
      if (this.hoverClose) {
        this.hoverTimeout = setTimeout(() => {
          this.closeDropdown();
        }, 500);
      }
    },
  },
  mounted() {
    this.setSelectedOptions(this.value);
    document.addEventListener("mousedown", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this.hoverTimeout);
  },
};
</script>

<style scoped>
span{
  width: 100%;
  height: 100%;
}
input {
  box-shadow: none;
  background-color: transparent !important;
  border: none;
  outline: none;
}
.custom-select-sm input::placeholder {
  font-family: Helvetica, Arial, sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
}

.custom-select-sm .icon-container .custom-select-arrow {
  font-size: 15px;
}


.custom-select {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--color-border-input);
  border-radius: 6px !important;
  color: var(--text-primary);
  padding: 7.5px 10px;
  cursor: pointer;
  background-color: var(--color-background-input);
  font-size: 15px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
}

.custom-select.input-style {
  font-size: 20px;
  height: 50px;
  padding: 0 15px;
  border: 2px solid;
  border-radius: 12px !important;
  box-sizing: border-box;
  outline-color: var(--color-border-input);
}

.custom-select.input-style .custom-select-input {
  font-size: 20px;
  padding: 0;
  margin: 0;
}

.custom-select.input-style .custom-select-input::placeholder {
  color: var(--color-border-input);
  font-family: 'Poppins', 'Inter', sans-serif;
}

.custom-select.input-style .custom-select-arrow {
  color: var(--text-primary);
}

.custom-select.input-style.has-value {
  color: var(--text-primary);
}

.custom-select.input-style > div {
  color: var(--text-primary);
  font-family: 'Poppins', 'Inter', sans-serif;
}

.custom-select.input-style.has-value > div {
  color: var(--text-primary);
}

/*noinspection CssUnusedSymbol */
.custom-select.disabled {
  pointer-events: none;
  background-color: var(--color-background-input);
  color: var(--text-primary);
  opacity: 50%;
  cursor: not-allowed;
}

.custom-select-input {
  padding: 0;
  margin: 0 0 0 5px;
  border: none;
  cursor: pointer;
  width: 100%;
  background-color: inherit;
}

.custom-select-input:disabled {
  background-color: var(--color-background-input);
  color: var(--text-primary);
  opacity: 50%;
  cursor: not-allowed;
}

.custom-select-input::placeholder {
  padding-left: 5px;
  color: var(--text-primary);
}

.custom-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: calc(100%);
  padding-left: 5px;
  border: 1px solid var(--color-border-input);
  border-radius: 6px;
  background-color: var(--color-background-input-no-opacity);
  max-height: 250px;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
}

.custom-select-option {
  padding: 8px;
  cursor: pointer;
}

/*noinspection CssUnusedSymbol */
.custom-select-option.disabled {
  pointer-events: none;
  color: var(--text-primary);
  cursor: not-allowed;
}

/*noinspection CssUnusedSymbol */
.custom-select-option:hover,
.custom-select-option.highlighted {
  background-color: lightgrey;
}

/*noinspection CssUnusedSymbol */
.custom-select-option.selected {
  font-weight: bold;
}

.custom-select-arrow {
  transition: transform 0.2s ease;
  color: var(--text-primary);
}

.icon-container {
  width: 15px;
  padding-right: 10px;
}

.custom-select-option-disabled {
  padding: 8px;
  cursor: not-allowed;
  color: var(--text-primary);
  opacity: 50%;
}

/*noinspection CssUnusedSymbol */
.custom-select-arrow.rotate {
  transform: rotate(180deg);
}
</style>
